import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  Eye,
  FileText,
  Layers,
  BookMarked,
  Link as LinkIcon,
  Target,
  Zap,
  Circle,
  Network
} from 'lucide-react';

export const SemanticModes: React.FC = () => {
  const [readingMode, setReadingMode] = useState<'normal' | 'semantic'>('normal');
  const [showAnnotations, setShowAnnotations] = useState(true);

  // Sample content with semantic annotations
  const sampleContent = {
    normal: {
      title: "Strategic Planning Framework",
      content: "Strategic planning involves defining organizational goals and creating a roadmap to achieve them. This process typically includes environmental analysis, goal setting, strategy development, and implementation planning."
    },
    semantic: {
      title: "Strategic Planning Framework",
      content: "Strategic planning involves defining organizational goals and creating a roadmap to achieve them. This process typically includes environmental analysis, goal setting, strategy development, and implementation planning.",
      annotations: [
        {
          id: '1',
          type: 'definition',
          text: 'Strategic planning',
          annotation: 'A systematic process for setting organizational direction and priorities',
          position: { start: 0, end: 19 }
        },
        {
          id: '2',
          type: 'relationship',
          text: 'organizational goals',
          annotation: 'Is-A: Strategic objectives that guide decision-making',
          position: { start: 45, end: 66 }
        },
        {
          id: '3',
          type: 'process',
          text: 'roadmap',
          annotation: 'Contains: Timeline, milestones, and resource allocation',
          position: { start: 75, end: 82 }
        },
        {
          id: '4',
          type: 'method',
          text: 'environmental analysis',
          annotation: 'Requires: Market research, competitor analysis, SWOT assessment',
          position: { start: 120, end: 142 }
        },
        {
          id: '5',
          type: 'method',
          text: 'goal setting',
          annotation: 'Enables: Clear direction and measurable outcomes',
          position: { start: 144, end: 155 }
        },
        {
          id: '6',
          type: 'method',
          text: 'strategy development',
          annotation: 'Derived-from: Goals and environmental analysis',
          position: { start: 157, end: 177 }
        },
        {
          id: '7',
          type: 'method',
          text: 'implementation planning',
          annotation: 'Precedes: Execution and monitoring phases',
          position: { start: 183, end: 205 }
        }
      ]
    }
  };

  const annotationTypes = [
    {
      id: 'definition',
      name: 'Definition',
      icon: BookOpen,
      color: 'blue',
      description: 'Explains what something means'
    },
    {
      id: 'relationship',
      name: 'Relationship',
      icon: Network,
      color: 'green',
      description: 'Shows how concepts relate to each other'
    },
    {
      id: 'process',
      name: 'Process',
      icon: Zap,
      color: 'purple',
      description: 'Describes how something works'
    },
    {
      id: 'method',
      name: 'Method',
      icon: Target,
      color: 'orange',
      description: 'Explains how to do something'
    },
    {
      id: 'example',
      name: 'Example',
      icon: Circle,
      color: 'teal',
      description: 'Provides concrete illustrations'
    },
    {
      id: 'reference',
      name: 'Reference',
      icon: LinkIcon,
      color: 'gray',
      description: 'Links to related information'
    }
  ];

  const relationshipTypes = [
    { symbol: '⊃', name: 'Is-A', description: 'Instance-of, type-of relationship' },
    { symbol: '⊂', name: 'Part-Of', description: 'Contained-by relationship' },
    { symbol: '→', name: 'Requires', description: 'Dependency requirement' },
    { symbol: '←', name: 'Enables', description: 'Capability enablement' },
    { symbol: '↗', name: 'Derived-From', description: 'Origin relationship' },
    { symbol: '≈', name: 'Analogous-To', description: 'Similarity relationship' },
    { symbol: '≠', name: 'Contrasts-With', description: 'Opposition relationship' },
    { symbol: '↔', name: 'Relates-To', description: 'General relationship' }
  ];

  const renderAnnotatedText = () => {
    if (readingMode === 'normal') {
      return (
        <div className="prose max-w-none">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-4">{sampleContent.normal.title}</h2>
          <p className="text-charcoal-700 leading-relaxed">{sampleContent.normal.content}</p>
        </div>
      );
    }

    const content = sampleContent.semantic;
    let text = content.content;
    const elements = [];

    // Sort annotations by position (reverse order to maintain indices)
    const sortedAnnotations = [...content.annotations].sort((a, b) => b.position.start - a.position.start);

    sortedAnnotations.forEach(annotation => {
      const before = text.substring(0, annotation.position.start);
      const annotated = text.substring(annotation.position.start, annotation.position.end);
      const after = text.substring(annotation.position.end);

      text = before;
      elements.unshift(
        <span key={`after-${annotation.id}`} className="text-charcoal-700">{after}</span>
      );
      elements.unshift(
        <span 
          key={annotation.id}
          className="relative inline-block"
        >
          <span className="bg-[var(--slate-dim)] text-[var(--text-sec)] px-1 rounded cursor-pointer hover:bg-[var(--slate-dim)] transition-colors">
            {annotated}
            <sup className="text-xs font-bold text-[var(--text-sec)] ml-1">{annotation.id}</sup>
          </span>
        </span>
      );
    });

    elements.unshift(<span key="before" className="text-charcoal-700">{text}</span>);

    return (
      <div className="prose max-w-none">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-4">{content.title}</h2>
        <p className="text-charcoal-700 leading-relaxed mb-6">
          {elements}
        </p>
        
        {showAnnotations && (
          <div className="bg-[var(--surface)] p-6 border border-[var(--rule)]">
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Semantic Annotations</h3>
            <div className="space-y-3">
              {content.annotations.map(annotation => {
                const type = annotationTypes.find(t => t.id === annotation.type);
                const Icon = type?.icon || FileText;
                return (
                  <div key={annotation.id} className="flex items-start gap-3 p-3 bg-[var(--bg-raised)] border border-[var(--rule)]">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-charcoal-500" />
                      <span className="text-sm font-bold text-charcoal-700">{annotation.id}</span>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-charcoal-900 mb-1">
                        "{annotation.text}"
                      </div>
                      <div className="text-sm text-charcoal-600">
                        {annotation.annotation}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/elevation/lab" className="inline-flex items-center gap-2 text-[var(--mustard)] hover:text-[var(--mustard)] mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to The Lab
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="px-4 py-2 font-bold text-xl bg-[var(--mustard-dim)] text-[var(--mustard)]">
            The Lab
          </div>
          <BookMarked className="h-10 w-10 text-[var(--mustard)]" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Semantic Reading Modes</h1>
            <p className="text-charcoal-600">Normal vs. Semantic reading with study Bible-style annotations</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          Explore how content can be presented in normal reading mode versus semantic mode with rich annotations, 
          similar to how study Bibles provide layered information and cross-references.
        </p>
      </div>

      {/* Mode Toggle */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-charcoal-900">Reading Mode</h2>
          <div className="flex bg-charcoal-100 p-1">
            <button
              onClick={() => setReadingMode('normal')}
              className={`px-4 py-2  text-sm font-medium transition-colors ${
                readingMode === 'normal'
                  ? 'bg-[var(--bg-raised)] text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Normal Reading
              </div>
            </button>
            <button
              onClick={() => setReadingMode('semantic')}
              className={`px-4 py-2  text-sm font-medium transition-colors ${
                readingMode === 'semantic'
                  ? 'bg-[var(--bg-raised)] text-charcoal-900 shadow-sm'
                  : 'text-charcoal-600 hover:text-charcoal-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Semantic Mode
              </div>
            </button>
          </div>
        </div>

        {readingMode === 'semantic' && (
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showAnnotations}
                onChange={(e) => setShowAnnotations(e.target.checked)}
                className="rounded border-[var(--rule)] text-[var(--mustard)] focus:ring-amber-500"
              />
              <span className="text-sm text-charcoal-700">Show annotation details</span>
            </label>
          </div>
        )}

        {/* Content Display */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          {renderAnnotatedText()}
        </div>
      </div>

      {/* Annotation Types */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-6">Annotation Types</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {annotationTypes.map(type => {
            const Icon = type.icon;
            return (
              <div key={type.id} className="p-4 border-2 border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <Icon className="h-6 w-6 text-charcoal-500" />
                  <h3 className="font-semibold text-charcoal-900">{type.name}</h3>
                </div>
                <p className="text-sm text-charcoal-600">{type.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Relationship Types */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <h2 className="text-xl font-semibold text-charcoal-900 mb-6">Relationship Types</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {relationshipTypes.map(rel => (
            <div key={rel.name} className="p-3 border border-[var(--rule)] bg-[var(--bg-raised)] text-center hover:border-[var(--rule)] transition-all">
              <div className="text-2xl font-bold text-charcoal-900 mb-2">{rel.symbol}</div>
              <div className="text-xs font-medium text-charcoal-700 mb-1">{rel.name}</div>
              <div className="text-xs text-charcoal-500">{rel.description}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Implementation Notes */}
      <div className="bg-sage-50 p-6 border border-sage-200">
        <h3 className="text-lg font-semibold text-sage-900 mb-3">Implementation Notes</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-sage-800 mb-2">Normal Reading Mode</h4>
            <ul className="text-sm text-sage-700 space-y-1">
              <li>• Clean, distraction-free text</li>
              <li>• Standard typography and layout</li>
              <li>• Focus on content flow and readability</li>
              <li>• Minimal visual elements</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-sage-800 mb-2">Semantic Reading Mode</h4>
            <ul className="text-sm text-sage-700 space-y-1">
              <li>• Rich annotations with superscripts</li>
              <li>• Hover states for quick information</li>
              <li>• Expandable annotation panels</li>
              <li>• Relationship indicators and cross-references</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
